import {RateCardUtil} from './RateCardUtil';
import moment from 'moment';
import {getTaskPlannedDate, getTaskRoleId} from './TaskUtil';

export class FixedPriceValueBasedTaskDistributor {
	constructor(taskList, fixedPrice, rateCard, statusColumns, redistributionCallback) {
		this.init(taskList, fixedPrice, rateCard, statusColumns, redistributionCallback);
	}

	init(taskList, fixedPrice, rateCard, statusColumns, redistributionCallback) {
		this.fixedPrice = fixedPrice;
		this.redistributionCallback = redistributionCallback;
		this.rateCardUtil = new RateCardUtil(rateCard);
		this.taskMap = new Map();
		this.statusColumnCategoryMap = new Map();
		this.totalPlannedValue = 0;
		this.totalForecastValue = 0;
		this.totalCalculatedActualRevenue = 0;
		if (statusColumns) {
			statusColumns.forEach(statusColumn => {
				this.statusColumnCategoryMap.set(statusColumn.id, statusColumn.category);
			});
		}
		this.addTasks(taskList);
	}

	addTask(task) {
		this.addTasks([task]);
	}

	addTasks(tasks) {
		for (const task of tasks) {
			const startDate = moment.utc({y: task.startYear, M: task.startMonth - 1, d: task.startDay});
			const endDate = moment.utc({y: task.deadlineYear, M: task.deadlineMonth - 1, d: task.deadlineDay});
			const taskHasValue = task.billable && task.approved && startDate && endDate && !startDate.isAfter(endDate);
			this.taskMap.set(task.id, {
				estimateForecast: task.estimateForecast,
				roleId: getTaskRoleId(task),
				plannedValue: taskHasValue ? this._calculateTaskPlannedValue(task) : 0,
				timeRegistrationValue: taskHasValue ? this._calculateTaskTimeRegistrationValue(task) : 0,
				forecastValue: taskHasValue ? this._calculateTaskForecastValue(task) : 0,
				done: task.done,
				calculatedPlannedRevenue: 0,
				calculatedActualRevenue: 0,
				timeRegistrations: task.timeRegistrations,
			});
		}
		this.redistribute();
	}

	updateTask(task) {
		this.addTask(task);
	}

	updateTasks(tasks) {
		this.addTasks(tasks);
	}

	updateEstimate(task, estimateForecast) {
		this.updateTask({...task, estimateForecast});
	}

	updateRole(task, roleId) {
		this.updateTask({...task, roleId});
	}

	updateApproved(task, approved) {
		this.updateTask({...task, approved});
	}

	updateStatus(task, statusColumnId) {
		const done = this.statusColumnCategoryMap.get(statusColumnId) === 'DONE';
		this.updateTask({...task, done});
	}

	deleteTask(taskId) {
		this.deleteTasks([taskId]);
	}

	deleteTasks(taskIds) {
		for (const taskId of taskIds) {
			this.taskMap.delete(taskId);
		}
		this.redistribute();
	}

	getTaskPlannedRevenue(taskId) {
		const task = this.taskMap.get(taskId) || {};
		return task.calculatedPlannedRevenue || 0;
	}

	getTaskActualRevenue(taskId) {
		const task = this.taskMap.get(taskId) || {};
		return task.calculatedActualRevenue || 0;
	}

	getTotalPlannedValue() {
		return this.totalPlannedValue;
	}

	getTotalForecastValue() {
		return this.totalForecastValue;
	}

	getTotalCalculatedActualRevenue() {
		return this.totalCalculatedActualRevenue || 0;
	}

	getFixedPrice() {
		return this.fixedPrice;
	}

	getTask(taskId) {
		return this.taskMap.get(taskId) || {};
	}

	redistribute() {
		const taskList = Array.from(this.taskMap.values());
		this.totalPlannedValue = taskList.reduce((total, task) => total + task.plannedValue, 0);
		this.totalForecastValue = taskList.reduce((total, task) => total + task.forecastValue, 0);
		this.totalCalculatedActualRevenue = 0;
		taskList.forEach(task => {
			task.calculatedPlannedRevenue =
				this.totalPlannedValue === 0 ? 0 : (task.plannedValue / this.totalPlannedValue) * this.fixedPrice;
			task.calculatedActualRevenue = (task.timeRegistrationValue / this.totalForecastValue) * this.fixedPrice;
			this.totalCalculatedActualRevenue += task.calculatedActualRevenue;
		});

		if (this.redistributionCallback) {
			this.redistributionCallback();
		}
	}

	_calculateTaskPlannedValue(task) {
		return (task.estimateForecast / 60) * this.rateCardUtil.getRate(getTaskRoleId(task), getTaskPlannedDate(task));
	}

	_calculateTaskTimeRegistrationValue(task) {
		let totalRegisteredTimesInHours = 0;
		if (task.timeRegistrations) {
			totalRegisteredTimesInHours =
				task.timeRegistrations.edges.reduce((total, edge) => total + edge.node.minutesRegistered, 0) / 60;
		}
		return totalRegisteredTimesInHours * this.rateCardUtil.getRate(getTaskRoleId(task), getTaskPlannedDate(task));
	}

	_calculateTaskForecastValue(task) {
		let totalRegisteredTimesInHours = 0;
		if (task.timeRegistrations) {
			totalRegisteredTimesInHours =
				task.timeRegistrations.edges.reduce((total, edge) => total + edge.node.minutesRegistered, 0) / 60;
		}
		return (
			(totalRegisteredTimesInHours + task.timeLeft / 60) *
			this.rateCardUtil.getRate(getTaskRoleId(task), getTaskPlannedDate(task))
		);
	}
}
